<template>
    <div class="grid grid-cols-1 gap-x-4 gap-y-3 py-3 sm:grid-cols-2">
        <div class="sm:col-span-1">
            <BaseInputText
                v-model="account.compliance_profiles.esac.financial_conditions.financial_assets"
                name="financial_conditions.financial_assets"
                :required="isRequiredNaturalComplianceProfileAttribute('financial_conditions', 'financial_assets')"
                :icon="CreditCardIcon"
                :label="$t('financial_conditions.financial_assets')"></BaseInputText>
        </div>
        <div class="sm:col-span-1">
            <BaseInputText
                v-model="account.compliance_profiles.esac.financial_conditions.annual_operating_result"
                name="financial_conditions.annual_operating_result"
                :required="
                    isRequiredNaturalComplianceProfileAttribute('financial_conditions', 'annual_operating_result')
                "
                :icon="CreditCardIcon"
                :label="$t('financial_conditions.annual_operating_result')"></BaseInputText>
        </div>
        <div class="sm:col-span-1">
            <BaseInputText
                v-model="account.compliance_profiles.esac.financial_conditions.dept_capital"
                :required="isRequiredNaturalComplianceProfileAttribute('financial_conditions', 'dept_capital')"
                name="financial_conditions.dept_capital"
                :icon="CreditCardIcon"
                :label="$t('financial_conditions.dept_capital')"></BaseInputText>
        </div>
        <div class="flex items-center pt-7 sm:col-span-1">
            <BaseInputCheckbox
                v-model="account.compliance_profiles.esac.financial_conditions.properties"
                :required="isRequiredNaturalComplianceProfileAttribute('financial_conditions', 'properties')"
                name="financial_conditions.properties"
                :label="$t('financial_conditions.properties')"></BaseInputCheckbox>
        </div>
        <div class="sm:col-span-1">
            <BaseInputText
                v-model="account.compliance_profiles.esac.financial_conditions.industry"
                :required="isRequiredNaturalComplianceProfileAttribute('financial_conditions', 'industry')"
                :icon="BriefcaseIcon"
                name="financial_conditions.industry"
                :label="$t('financial_conditions.industry')"></BaseInputText>
        </div>
    </div>

    <div class="grid grid-cols-1 gap-x-4 gap-y-3 py-3 sm:grid-cols-2">
        <div class="pb-2 sm:col-span-2">
            <h3
                :class="[
                    'text-base font-bold text-gray-700',
                    { 'required': isRequiredNaturalComplianceProfileAttribute('financial_conditions', 'industry') },
                ]">
                {{ $t('financial_conditions.source_of_funds') }}
            </h3>
        </div>
        <div class="sm:col-span-1">
            <BaseInputCheckbox
                v-model="account.compliance_profiles.esac.financial_conditions.source_of_funds.dividends_or_profits"
                name="financial_conditions.source_of_funds.dividends_or_profits"
                :label="$t('financial_conditions.dividends_or_profits')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputCheckbox
                v-model="account.compliance_profiles.esac.financial_conditions.source_of_funds.lottery_gambling"
                name="financial_conditions.source_of_funds.lottery_gambling"
                :label="$t('financial_conditions.lottery_gambling')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputCheckbox
                v-model="account.compliance_profiles.esac.financial_conditions.source_of_funds.divorce"
                name="financial_conditions.source_of_funds.divorce"
                :label="$t('financial_conditions.divorce')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputCheckbox
                v-model="account.compliance_profiles.esac.financial_conditions.source_of_funds.maturity_or_surrender"
                name="financial_conditions.source_of_funds.maturity_or_surrender"
                :label="$t('financial_conditions.maturity_or_surrender')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputCheckbox
                v-model="account.compliance_profiles.esac.financial_conditions.source_of_funds.gift"
                name="financial_conditions.source_of_funds.gift"
                :label="$t('financial_conditions.gift')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputCheckbox
                v-model="account.compliance_profiles.esac.financial_conditions.source_of_funds.inheritance"
                name="financial_conditions.source_of_funds.inheritance"
                :label="$t('financial_conditions.inheritance')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputCheckbox
                v-model="account.compliance_profiles.esac.financial_conditions.source_of_funds.insurance_claims"
                name="financial_conditions.source_of_funds.insurance_claims"
                :label="$t('financial_conditions.insurance_claims')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputCheckbox
                v-model="account.compliance_profiles.esac.financial_conditions.source_of_funds.sale_of_property"
                name="financial_conditions.source_of_funds.sale_of_property"
                :label="$t('financial_conditions.sale_of_property')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputCheckbox
                v-model="account.compliance_profiles.esac.financial_conditions.source_of_funds.loan"
                name="financial_conditions.source_of_funds.loan"
                :label="$t('financial_conditions.loan')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputCheckbox
                v-model="account.compliance_profiles.esac.financial_conditions.source_of_funds.savings"
                name="financial_conditions.source_of_funds.savings"
                :label="$t('financial_conditions.savings')" />
        </div>
        <div class="sm:col-span-1">
            <BaseInputTextActivatable
                v-model="account.compliance_profiles.esac.financial_conditions.source_of_funds.other"
                name="financial_conditions.source_of_funds.other"
                :label="$t('financial_conditions.other')"></BaseInputTextActivatable>
        </div>
    </div>
</template>

<script setup lang="ts">
import { usePersonalAccountDataState } from '../../../../utilities/account/useAccountDataPersonal'
import BaseInputTextActivatable from '../../../base/BaseInputTextActivatable.vue'
import BaseInputCheckbox from '../../../base/BaseInputCheckbox.vue'
import BaseInputText from '../../../base/BaseInputText.vue'
import {
    getNaturalWidgetFormValidationRules,
    initializeNaturalWidgetForm,
    isRequiredNaturalComplianceProfileAttribute,
} from './forms'
import { BriefcaseIcon, CreditCardIcon } from '@heroicons/vue/solid'

const { naturalProfile: account } = usePersonalAccountDataState()
initializeNaturalWidgetForm(getNaturalWidgetFormValidationRules('financial_conditions'))
</script>
